import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Queue as QueueIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Description as FileIcon,
  Replay as ReplayIcon
} from '@mui/icons-material';
import { useWebSocket } from '../WebSocketContext';

const formatTimestamp = (timestamp) => {
  return new Date(timestamp * 1000).toLocaleTimeString();
};

const formatFilePath = (path) => {
  const parts = path.split(/[/\\]/);
  if (parts.length > 3) {
    return '...' + parts.slice(-3).join('/');
  }
  return path;
};

const ActiveTasksStatus = () => {
  const { sendMessage, subscribeToMessages } = useWebSocket();
  const [tasksInfo, setTasksInfo] = useState({
    active_tasks_count: 0,
    queue_size: 0,
    files_in_progress_count: 0,
    max_workers: 0,
    files_in_progress: [],
    queue_files: [],
    progress_files_returned: 0,
    progress_files_total: 0,
    is_progress_truncated: false,
    queue_files_returned: 0,
    queue_files_total: 0,
    is_queue_truncated: false,
    post_processing: {
      queue_size: 0,
      status: 'idle',
      is_processing: false,
      current_item: null,
      manual_replays: [],
      manual_replays_active: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const requestActiveTasksInfo = useCallback(() => {
    if (typeof sendMessage === 'function') {
      sendMessage({ command: 'get_active_tasks_info', data: {} });
    }
  }, [sendMessage]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      if (parsedMessage.command === 'active_tasks_info') {
        setTasksInfo(parsedMessage.data);
        setLoading(false);
        if (parsedMessage.data.error) {
          setError(parsedMessage.data.error);
        } else {
          setError(null);
        }
      }
    });

    return unsubscribe;
  }, [subscribeToMessages]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        requestActiveTasksInfo();
      }, 5000);

      setLoading(true);
      requestActiveTasksInfo();

      return () => clearInterval(interval);
    }
  }, [autoRefresh, requestActiveTasksInfo]);

  // Calculate progress info
  const processingFiles = tasksInfo.files_in_progress_count;
  const queuedFiles = tasksInfo.queue_size;
  const postProcessingInfo = tasksInfo.post_processing || {};
  const postProcessingQueued = postProcessingInfo.queue_size || 0;
  const postProcessingActive = postProcessingInfo.current_item ? 1 : 0;
  const manualReplays = postProcessingInfo.manual_replays || [];
  const manualReplayActive = Number(postProcessingInfo.manual_replays_active ?? manualReplays.length ?? 0);
  
  const totalRemainingFiles = processingFiles + queuedFiles + postProcessingQueued + postProcessingActive + manualReplayActive;
  const totalActiveFiles = processingFiles + postProcessingActive + manualReplayActive;
  const hasPostProcessingInfo = !!tasksInfo.post_processing && (
    postProcessingQueued > 0 ||
    postProcessingActive > 0 ||
    postProcessingInfo.is_processing ||
    postProcessingInfo.current_item ||
    manualReplays.length > 0
  );
  
  let percentage = 100;
  let hasActivity = false;
  let isComplete = true;
  
  if (totalRemainingFiles > 0) {
    const activeWorkPercentage = (totalActiveFiles / totalRemainingFiles) * 100;
    percentage = Math.round(Math.min(activeWorkPercentage * 2, 100));
    hasActivity = true;
    isComplete = false;
  }

  if (loading && tasksInfo.active_tasks_count === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={3}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ ml: 1 }}>Loading tasks info...</Typography>
      </Box>
    );
  }

  const hasNoActivity = tasksInfo.active_tasks_count === 0 && 
                        queuedFiles === 0 && 
                        processingFiles === 0 &&
                        postProcessingQueued === 0 &&
                        postProcessingActive === 0 &&
                        manualReplayActive === 0;

  return (
    <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <PlayArrowIcon sx={{ mr: 1, color: 'primary.main' }} />
            Active Tasks Status
          </Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton 
                onClick={requestActiveTasksInfo} 
                disabled={loading}
                size="small"
              >
                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading tasks info: {error}
          </Alert>
        )}

        {/* Summary Chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`${tasksInfo.active_tasks_count} Active Tasks`}
            color={tasksInfo.active_tasks_count > 0 ? 'primary' : 'default'}
            size="small"
          />
          <Chip
            label={`${tasksInfo.queue_size} Queued Events`}
            color={tasksInfo.queue_size > 0 ? 'warning' : 'default'}
            size="small"
          />
          <Chip
            label={`${tasksInfo.files_in_progress_count} Processing`}
            color={tasksInfo.files_in_progress_count > 0 ? 'success' : 'default'}
            size="small"
          />
          <Chip
            label={`Max Workers: ${tasksInfo.max_workers}`}
            color="default"
            size="small"
          />
          <Chip
            label={`Post-Processing: ${postProcessingQueued} queued${postProcessingActive ? ', 1 active' : ''}${manualReplays.length ? `, ${manualReplays.length} manual` : ''}`}
            color={postProcessingInfo.is_processing || manualReplayActive > 0 ? 'secondary' : 'default'}
            size="small"
          />
          {manualReplayActive > 0 && (
            <Chip
              label={`${manualReplayActive} Manual Replay${manualReplayActive > 1 ? 's' : ''}`}
              color="secondary"
              size="small"
            />
          )}
        </Box>

        {/* Overall Progress Bar */}
        {hasActivity && (
          <Card sx={{ 
            mb: 2, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.1) 75%, rgba(255,255,255,0.1))',
              backgroundSize: '20px 20px',
              animation: processingFiles > 0 ? 'progress-stripes 1s linear infinite' : 'none',
              '@keyframes progress-stripes': {
                '0%': { transform: 'translateX(0)' },
                '100%': { transform: 'translateX(20px)' }
              }
            }
          }}>
            <CardContent sx={{ position: 'relative', zIndex: 1, pb: '16px !important' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlayArrowIcon sx={{ color: 'rgba(255,255,255,0.9)' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>
                    {isComplete ? 'All Tasks Complete' : 'Processing Pipeline'}
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: 2,
                  px: 2,
                  py: 0.5
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CircularProgress size={12} sx={{ color: '#4caf50' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {processingFiles} events
                    </Typography>
                  </Box>
                  {postProcessingActive > 0 && (
                    <>
                      <Box sx={{ width: 1, height: 16, bgcolor: 'rgba(255,255,255,0.3)' }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CircularProgress size={12} sx={{ color: '#9c27b0' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {postProcessingActive} post-proc
                        </Typography>
                      </Box>
                    </>
                  )}
                  <Box sx={{ width: 1, height: 16, bgcolor: 'rgba(255,255,255,0.3)' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <QueueIcon sx={{ fontSize: 14, color: '#ff9800' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {queuedFiles + postProcessingQueued} waiting
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  borderRadius: 2, 
                  height: 12,
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${percentage}%`,
                      background: percentage < 30 
                        ? 'linear-gradient(90deg, #ff6b6b, #ff8e8e)' 
                        : percentage < 70 
                          ? 'linear-gradient(90deg, #ffd93d, #ffed4e)' 
                          : 'linear-gradient(90deg, #6bcf7f, #4caf50)',
                      borderRadius: 2,
                      transition: 'width 0.3s ease-in-out',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                        animation: processingFiles > 0 ? 'progress-shine 2s ease-in-out infinite' : 'none',
                        '@keyframes progress-shine': {
                          '0%': { transform: 'translateX(-100%)' },
                          '100%': { transform: 'translateX(200%)' }
                        }
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>
                  {isComplete 
                    ? 'All events processed successfully'
                    : `Pipeline Efficiency: ${percentage}% (${totalActiveFiles} active)`
                  }
                </Typography>
                <Typography variant="caption" sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  px: 1.5, 
                  py: 0.5, 
                  borderRadius: 1,
                  fontWeight: 500
                }}>
                  {isComplete 
                    ? 'Complete'
                    : `${totalRemainingFiles} total remaining`
                  }
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Events Currently Being Processed */}
        {tasksInfo.files_in_progress && tasksInfo.files_in_progress.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                <PlayArrowIcon sx={{ mr: 1, color: 'success.main' }} />
                Events Currently Processing ({tasksInfo.progress_files_returned}
                {tasksInfo.is_progress_truncated && ` of ${tasksInfo.progress_files_total}`})
                {tasksInfo.is_progress_truncated && (
                  <Chip 
                    label="Truncated" 
                    size="small" 
                    color="warning" 
                    sx={{ ml: 1 }} 
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                {tasksInfo.files_in_progress.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <FileIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.filename}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            Path: {formatFilePath(file.path)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Project IDs: {file.case_ids.join(', ')}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Started: {formatTimestamp(file.timestamp)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Events in Queue */}
        {tasksInfo.queue_files && tasksInfo.queue_files.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                <QueueIcon sx={{ mr: 1, color: 'warning.main' }} />
                Events in Queue ({tasksInfo.queue_files_returned}
                {tasksInfo.is_queue_truncated && ` of ${tasksInfo.queue_files_total}`})
                {tasksInfo.is_queue_truncated && (
                  <Chip 
                    label="Truncated" 
                    size="small" 
                    color="warning" 
                    sx={{ ml: 1 }} 
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                {tasksInfo.queue_files.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <FileIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.filename}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            Path: {formatFilePath(file.path)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Project IDs: {file.case_ids.join(', ')}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Queued: {formatTimestamp(file.timestamp)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
                {tasksInfo.is_queue_truncated && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="caption" color="text.secondary">
                          ... and {tasksInfo.queue_files_total - tasksInfo.queue_files_returned} more events (showing first {tasksInfo.queue_files_returned})
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Post-Processing Status */}
        {hasPostProcessingInfo && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                <PlayArrowIcon sx={{ mr: 1, color: 'secondary.main' }} />
                Post-Processing Queue ({postProcessingQueued} queued{postProcessingActive ? ', 1 active' : ''}{manualReplays.length ? `, ${manualReplays.length} manual replay${manualReplays.length > 1 ? 's' : ''}` : ''}, Status: {postProcessingInfo.status})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {postProcessingInfo.current_item && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
                      Currently Processing:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Case:</strong> {postProcessingInfo.current_item.case_name} (ID: {postProcessingInfo.current_item.case_id})
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Type:</strong> {postProcessingInfo.current_item.type}
                      {postProcessingInfo.current_item.batch_size && ` (${postProcessingInfo.current_item.batch_size} items)`}
                    </Typography>
                    {postProcessingInfo.current_item.timeevent_id && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Time Event ID:</strong> {postProcessingInfo.current_item.timeevent_id}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Started:</strong> {formatTimestamp(postProcessingInfo.current_item.start_time)}
                    </Typography>
                    {postProcessingInfo.processing_duration && (
                      <Typography variant="body2">
                        <strong>Duration:</strong> {Math.round(postProcessingInfo.processing_duration)}s
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Queue Items List */}
                {postProcessingInfo.queue_details && postProcessingInfo.queue_details.length > 0 && (() => {
                  // Detect potential duplicates
                  const caseOccurrences = {};
                  postProcessingInfo.queue_details.forEach(item => {
                    const key = `${item.case_id}`;
                    caseOccurrences[key] = (caseOccurrences[key] || 0) + 1;
                  });
                  const hasDuplicates = Object.values(caseOccurrences).some(count => count > 1);
                  
                  return (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Items in Memory Buffer ({postProcessingInfo.queue_details.length}
                        {postProcessingInfo.in_memory_buffer > postProcessingInfo.queue_details.length && 
                          ` of ${postProcessingInfo.in_memory_buffer}`}):
                      </Typography>
                      {hasDuplicates && (
                        <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                          <Typography variant="caption">
                            ⚠️ Duplicate case entries detected. Same case may be processed multiple times.
                          </Typography>
                        </Alert>
                      )}
                      <List dense>
                        {postProcessingInfo.queue_details.map((item, index) => {
                          const isDuplicate = caseOccurrences[`${item.case_id}`] > 1;
                          return (
                            <ListItem 
                              key={index} 
                              sx={{ 
                                bgcolor: index % 2 === 0 ? '#f5f5f5' : 'transparent',
                                borderLeft: isDuplicate ? '3px solid #ff9800' : 'none',
                                paddingLeft: isDuplicate ? 1 : 2
                              }}
                            >
                              <ListItemIcon>
                                <FileIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <span>{item.case_name} (Project ID: {item.case_id})</span>
                                    {isDuplicate && (
                                      <Chip 
                                        label={`×${caseOccurrences[`${item.case_id}`]}`} 
                                        size="small" 
                                        color="warning" 
                                        sx={{ height: 18, fontSize: '0.7rem' }}
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    <Typography variant="caption" display="block">
                                      Type: {item.type}
                                      {item.timeevent_count && ` - ${item.timeevent_count} time events`}
                                      {item.timeevent_id && ` - Time Event ID: ${item.timeevent_id}`}
                                    </Typography>
                                    {item.widget_id && (
                                      <Typography variant="caption" display="block">
                                        Widget ID: {item.widget_id}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                      {postProcessingInfo.in_memory_buffer > postProcessingInfo.queue_details.length && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          ... and {postProcessingInfo.in_memory_buffer - postProcessingInfo.queue_details.length} more items in buffer (showing first {postProcessingInfo.queue_details.length})
                        </Typography>
                      )}
                    </Box>
                  );
                })()}

                {/* Summary Information */}
                {!postProcessingInfo.current_item && postProcessingQueued === 0 && manualReplays.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No items in post-processing queue
                  </Typography>
                )}
                
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total Queue Size:</strong> {postProcessingQueued} items
                  {postProcessingInfo.in_memory_buffer !== undefined && 
                    ` (${postProcessingInfo.in_memory_buffer} buffered in memory)`}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Status:</strong> {postProcessingInfo.status}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Currently Processing:</strong> {postProcessingInfo.is_processing ? 'Yes' : 'No'}
                </Typography>
                {postProcessingInfo.has_processing_task !== undefined && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Processing Task Active:</strong> {postProcessingInfo.has_processing_task ? 'Yes' : 'No'}
                  </Typography>
                )}
                {postProcessingInfo.persisted_breakdown && (
                  <Box sx={{ mt: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                      Persisted Queue Breakdown:
                    </Typography>
                    <Typography variant="caption" display="block">
                      Total: {postProcessingInfo.persisted_breakdown.total} | 
                      Queued: {postProcessingInfo.persisted_breakdown.queued} | 
                      Buffered: {postProcessingInfo.persisted_breakdown.buffered} | 
                      Processing: {postProcessingInfo.persisted_breakdown.processing}
                    </Typography>
                    {postProcessingInfo.persisted_breakdown.oldest_created_at && (
                      <Typography variant="caption" display="block">
                        Oldest Entry: {new Date(postProcessingInfo.persisted_breakdown.oldest_created_at * 1000).toLocaleString()}
                      </Typography>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                      Note: List above shows in-memory buffer only. Full queue is persisted to database.
                    </Typography>
                  </Box>
                )}
                {postProcessingInfo.in_memory_buffer === undefined && postProcessingInfo.queue_details && postProcessingInfo.queue_details.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Showing sample of queue items
                  </Typography>
                )}
                {postProcessingInfo.error && (
                  <Typography variant="body2" color="error">
                    <strong>Error:</strong> {postProcessingInfo.error}
                  </Typography>
                )}
                {manualReplays.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ReplayIcon sx={{ mr: 1, fontSize: 18 }} />
                      Manual Workflow Replays ({manualReplays.length} running)
                    </Typography>
                    <List dense>
                      {manualReplays.map((replay) => {
                        const workflowProgress = replay.workflows_total
                          ? Math.round((replay.workflows_completed / replay.workflows_total) * 100)
                          : 0;
                        const eventProgress = replay.timeevents_total
                          ? Math.round((replay.processed_timeevents / replay.timeevents_total) * 100)
                          : 0;
                        return (
                          <ListItem key={`${replay.case_id}-${replay.started_at}`}>
                            <ListItemText
                              primary={`${replay.case_name || `Case ${replay.case_id}`} (${workflowProgress}% workflows, ${eventProgress}% events)`}
                              secondary={
                                <Box>
                                  <Typography variant="caption" display="block">
                                    Workflows: {replay.workflows_completed}/{replay.workflows_total} | Failed: {replay.workflows_failed}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Time Events: {replay.processed_timeevents}/{replay.timeevents_total}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Mode: {replay.execution_mode}
                                  </Typography>
                                  {replay.current_workflow_name && (
                                    <Typography variant="caption" display="block">
                                      Current Workflow: {replay.current_workflow_name}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" display="block">
                                    Started: {formatTimestamp(replay.started_at)}
                                  </Typography>
                                  {replay.requested_by && (
                                    <Typography variant="caption" display="block">
                                      Requested By: User #{replay.requested_by}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Post-processing handles actions and workflows after events are imported
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Empty State */}
        {hasNoActivity && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No active tasks or queued events
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ActiveTasksStatus;
